import { injectStrict } from "@/utils/utils";
import { computed, inject } from "vue";
import { useRoute } from "vue-router";
import type { FormItems } from "@/composables/forms/formTypes";
import {
  scenarioFormKey,
  sizingFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { equipmentPreviewKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import useUserInfo from "./useUserInfo";

export function useEditPermissions(
  sizingForm?: FormItems["sizing"],
  scenarioForm?: FormItems["scenario"] | null // null passed as arg for before scenario is created
) {
  const sizing = sizingForm || injectStrict(sizingFormKey);
  const scenario =
    scenarioForm === null
      ? null
      : scenarioForm || injectStrict(scenarioFormKey);

  const userInfo = useUserInfo();
  const isEquipmentPreview = inject(equipmentPreviewKey, false);
  const route = useRoute();

  const isNewScenario = computed(() => route?.params?.scenarioId === "new");
  const isNewSizing = computed(() => route?.params?.sizingId === "new");

  const isSizingReadonly = computed(() => {
    if (isEquipmentPreview) return true;
    if (userInfo.has_elevated_privileges || isNewSizing.value) {
      return false;
    }
    if (!sizing.data.value?.journal_author_id || !userInfo.id) return true;
    return sizing.data.value?.journal_author_id !== userInfo.id;
  });

  const isScenarioReadonly = computed(() => {
    if (isEquipmentPreview) return true;
    if (
      userInfo.has_elevated_privileges ||
      isNewScenario.value ||
      scenario === null
    ) {
      return false;
    }
    if (!scenario.data.value?.journal_author_id || !userInfo.id) return true;
    return scenario.data.value?.journal_author_id !== userInfo.id;
  });

  const isReadonly = computed(
    () => isSizingReadonly.value || isScenarioReadonly.value
  );

  const isImportedFromLegacy = computed(
    () => sizing.data.value?.journal_author_id === "import-user"
  );

  return {
    isSizingReadonly,
    isScenarioReadonly,
    isReadonly,
    isImportedFromLegacy
  };
}
